import React from 'react';
import { Star, CheckCircle, Video, Image as ImageIcon, Heart, Building, Edit, Trash2, Share2, Play } from 'lucide-react';

interface ReviewCardProps {
  review: {
    id: string;
    author: string;
    company: string;
    companySize: string;
    rating: number;
    comment: string;
    product: string;
    sector: string;
    hasVideo: boolean;
    hasImage: boolean;
    videoUrl?: string | null;
    videoTitle?: string;
    videoDescription?: string;
    videoTags?: string[];
    date: string;
    featured: boolean;
    verified: boolean;
    helpful: number;
    companyResponse: string | null;
  };
  onPlayVideo: (url: string, title?: string, description?: string, tags?: string[]) => void;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, onPlayVideo }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold shadow-sm">
            {review.author.split(' ').map(n => n[0]).join('').substring(0, 2)}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h4 className="font-semibold text-gray-900">{review.author}</h4>
              {review.verified && (
                <div className="text-green-500" title="Verified Review">
                  <CheckCircle className="w-4 h-4" />
                </div>
              )}
              {review.featured && (
                <div className="text-yellow-500" title="Featured Review">
                  <Star className="w-4 h-4 fill-current" />
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600">{review.company} • {review.companySize}</p>
            <div className="flex items-center space-x-2 mt-1">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500">
                {new Date(review.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {review.hasImage && (
            <div className="p-2 bg-green-100 rounded-lg" title="Has Image">
              <ImageIcon className="w-4 h-4 text-green-600" />
            </div>
          )}
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700">
            <Edit className="w-4 h-4" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-red-500 hover:text-red-700">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-gray-700 italic leading-relaxed">"{review.comment}"</p>
        
        {/* Video Title and Description */}
        {review.hasVideo && review.videoTitle && (
          <div className="mt-3 mb-2">
            <h4 className="font-semibold text-gray-900 text-sm line-clamp-1">
              {review.videoTitle}
            </h4>
          </div>
        )}
        {review.hasVideo && review.videoDescription && (
          <div className="mb-2">
            <p className="text-xs text-gray-600 line-clamp-1">
              {review.videoDescription}
            </p>
          </div>
        )}
        {review.hasVideo && review.videoTags && review.videoTags.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {review.videoTags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                {tag}
              </span>
            ))}
            {review.videoTags.length > 3 && (
              <span className="text-xs text-gray-500">+{review.videoTags.length - 3}</span>
            )}
          </div>
        )}
        
        {/* Video Splash */}
        {review.hasVideo && review.videoUrl && (
          <div 
            onClick={() => onPlayVideo(review.videoUrl!, review.videoTitle, review.videoDescription, review.videoTags)}
            className="mt-4 relative w-full h-48 bg-gray-900 rounded-lg overflow-hidden cursor-pointer group group-hover:shadow-lg transition-all"
          >
            {/* Abstract Gradient Background since we don't have thumbnail */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 to-purple-800 opacity-80 group-hover:opacity-100 transition-opacity"></div>
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Play className="w-5 h-5 text-purple-600 ml-1" />
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-3 left-4 text-white text-sm font-medium flex items-center">
              <Video className="w-4 h-4 mr-2" />
              Watch Video Testimonial
            </div>
          </div>
        )}

        <div className="flex items-center space-x-4 mt-4 text-sm text-gray-500">
          <span className="font-medium text-gray-700">Product: {review.product}</span>
          <span>•</span>
          <button className="flex items-center space-x-1 hover:text-red-500 transition-colors">
            <Heart className="w-3 h-3" />
            <span>{review.helpful} helpful</span>
          </button>
        </div>
      </div>

      {review.companyResponse && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mt-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
              <Building className="w-3 h-3 text-white" />
            </div>
            <span className="font-medium text-blue-900">Response</span>
          </div>
          <p className="text-blue-800 text-sm">{review.companyResponse}</p>
        </div>
      )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-3">
          <button
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              review.featured 
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Star className="w-3 h-3" />
            <span>{review.featured ? 'Featured' : 'Feature'}</span>
          </button>
          
          <button className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium hover:bg-gray-200 transition-colors">
            <Share2 className="w-3 h-3" />
            <span>Share</span>
          </button>
        </div>
        
        {review.sector && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {review.sector}
          </span>
        )}
      </div>
    </div>
  );
};

export default ReviewCard;
